import { 
  Share2, MessageCircle, Eye, Clock, AlertTriangle, 
  FileCheck, XCircle, BadgeCheck, Banknote, 
  Building2
} from "lucide-react";
import  {LenderStatus}  from "../types/deals";

// Centralized status configuration
export const STATUS_CONFIG = {
  "1": { 
    icon: Share2, 
    variant: "info" as const, 
    color: "text-blue-600",
    description: "Deal has been shared with lender"
  },
  "2": { 
    icon: MessageCircle, 
    variant: "purple" as const, 
    color: "text-purple-600",
    description: "Initial credit discussion completed"
  },
  "3": { 
    icon: Eye, 
    variant: "warning" as const, 
    color: "text-orange-600",
    description: "Application under lender review"
  },
  "4": { 
    icon: Clock, 
    variant: "outline" as const, 
    color: "text-gray-600",
    description: "Lender not responding"
  },
  "5": { 
    icon: AlertTriangle, 
    variant: "warning" as const, 
    color: "text-yellow-600",
    description: "On Hold"
  },
  "6": { 
    icon: FileCheck, 
    variant: "info" as const, 
    color: "text-indigo-600",
    description: "Term sheet received from lender"
  },
  "7": { 
    icon: XCircle, 
    variant: "destructive" as const, 
    color: "text-white",
    description: "Application rejected"
  },
  "8": { 
    icon: BadgeCheck, 
    variant: "success" as const, 
    color: "text-green-600",
    description: "Deal sanctioned by lender"
  },
  "9": { 
    icon: Banknote, 
    variant: "success" as const, 
    color: "text-emerald-600",
    description: "Amount disbursed"
  },
  "Disbursed": { 
    icon: Banknote, 
    variant: "success" as const, 
    color: "text-emerald-600",
    description: "Amount disbursed"
  },
  "Sanctioned": { 
    icon: BadgeCheck, 
    variant: "success" as const, 
    color: "text-green-600",
    description: "Deal sanctioned by lender"
  },
  "Deal Shared": { 
    icon: Share2, 
    variant: "info" as const, 
    color: "text-blue-600",
    description: "Deal has been shared with lender"
  },
  "Credit Discussion Completed": { 
    icon: MessageCircle, 
    variant: "purple" as const, 
    color: "text-purple-600",
    description: "Initial credit discussion completed"
  },
  "Under Review": { 
    icon: Eye, 
    variant: "warning" as const, 
    color: "text-orange-600",
    description: "Application under lender review"
  },
  "Unresponsive": { 
    icon: Clock, 
    variant: "outline" as const, 
    color: "text-gray-600",
    description: "Lender not responding"
  },
  "On Hold": { 
    icon: AlertTriangle, 
    variant: "warning" as const, 
    color: "text-yellow-600",
    description: "On Hold"
  },
  "Rejected": { 
    icon: XCircle, 
    variant: "destructive" as const, 
    color: "text-white",
    description: "Application rejected"
  },
  "Term Sheet Shared": { 
    icon: FileCheck, 
    variant: "info" as const, 
    color: "text-indigo-600",
    description: "Term sheet received from lender"
  },
  "No Lenders": { 
    icon: Building2, 
    variant: "info" as const, 
    color: "text-blue-600",
    description: "No lenders tagged yet"
  },
} as const;

export function getStatusConfig(status: LenderStatus | null | undefined) {
  if (!status) return null;
  return STATUS_CONFIG[status as keyof typeof STATUS_CONFIG] || null;
}

export function calculatePayouts(
  baseAmount: number,
  payoutMode: "percentage" | "manual",
  values: {
    customerPayout?: number;
    lenderPayout?: number;
    partnerPayout?: number;
    customerPayoutPercent?: number;
    lenderPayoutPercent?: number;
    partnerPayoutPercent?: number;
  }
) {
  if (payoutMode === "manual") {
    return {
      customerPayout: values.customerPayout ?? 0,
      lenderPayout: values.lenderPayout ?? 0,
      partnerPayout: values.partnerPayout ?? 0,
    };
  }

  return {
    customerPayout: ((values.customerPayoutPercent ?? 0) / 100) * baseAmount,
    lenderPayout: ((values.lenderPayoutPercent ?? 0) / 100) * baseAmount,
    partnerPayout: ((values.partnerPayoutPercent ?? 0) / 100) * baseAmount,
  };
}

export function validateStatusTransition(
  currentStatus: string | null | undefined,
  newStatus: string
): { valid: boolean; message?: string } {
  if (!currentStatus) return { valid: true };

  // Case 1: Disbursed is final — no changes allowed
  if (currentStatus === "9") {
    return {
      valid: newStatus === "9", // only itself allowed
      message: "Disbursed is final and cannot be changed.",
    };
  }

  // Case 2: Sanctioned can only move to 7, 5, 9
  if (currentStatus === "8" && !["7", "5", "9"].includes(newStatus)) {
    return {
      valid: false,
      message: "Sanctioned can only move to Rejected, On Hold, or Disbursed.",
    };
  }

  if (currentStatus != "8" && newStatus === "9") {
    return {
      valid: false,
      message: "Sanctioned can only move to Rejected, On Hold, or Disbursed.",
    };
  }

  // Otherwise all moves allowed
  return { valid: true };
}